import { makeStyles } from '@material-ui/core/styles'
import { chatDrawerSelectors } from 'ducks/chatDrawer'
import { keyframes, styled, Typography } from 'opensolar-ui'
import React from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import AdaShimmerText from './AdaShimmerText'
import { AutoDesignProgressItem } from './tools/autoDesign/AutoDesignProgressItem'
const useStyles = makeStyles(() => ({
  voiceIndicatorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    padding: '20px',
    backgroundColor: '#f8f9ff',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    minHeight: '80px',
    gap: '8px',
    boxSizing: 'border-box',
  },
  indicatorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: '#1751D0',
    fontSize: 14,
    fontWeight: 500,
  },
  dotsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  listeningDotsContainer: {
    gap: 3,
  },
  thinkingDotsContainer: {
    gap: 4,
  },
}))

interface VoiceIndicatorProps {
  isListening: boolean
  isResponding: boolean
  isPreparingResponse?: boolean
  reasoningStatus?: string | null
  speakingTranscript?: string | null
  toolCall?: any | null
  isSessionActive?: boolean
}

const AdaAnimatedDots: React.FC<{ type: 'listening' | 'thinking' | 'offline' | 'ready'; count?: number }> = ({
  type,
  count = 3,
}) => {
  const classes = useStyles()

  return (
    <>
      <style>
        {`
          @keyframes listening {
            0% { height: 8px; }
            100% { height: 20px; }
          }
          @keyframes thinking {
            0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
            40% { opacity: 1; transform: scale(1); }
          }
          @keyframes adaVoicePulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
          }
          @keyframes ready {
            0%, 100% { opacity: 0.4; transform: scale(0.9); }
            50% { opacity: 1; transform: scale(1.1); }
          }
        `}
      </style>
      <div
        className={`${classes.dotsContainer} ${
          type === 'listening'
            ? classes.listeningDotsContainer
            : type === 'ready'
              ? classes.thinkingDotsContainer
              : classes.thinkingDotsContainer
        }`}
      >
        {Array.from({ length: type === 'offline' ? 1 : count }, (_, i) => (
          <div
            key={i}
            style={{
              width: type === 'offline' ? 12 : type === 'listening' ? 4 : type === 'ready' ? 6 : 8,
              height: type === 'offline' ? 12 : type === 'listening' ? 16 : type === 'ready' ? 6 : 8,
              backgroundColor: '#1751D0',
              borderRadius: type === 'listening' ? 2 : '50%',
              animation:
                type === 'offline'
                  ? 'adaVoicePulse 2s ease-in-out infinite'
                  : type === 'listening'
                    ? `listening ${0.6 + i * 0.1}s ease-in-out infinite alternate`
                    : type === 'ready'
                      ? `ready 1.2s ease-in-out infinite`
                      : `thinking 1.4s ease-in-out infinite`,
              animationDelay: type === 'thinking' ? `${i * 0.2}s` : undefined,
            }}
          />
        ))}
      </div>
    </>
  )
}

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const ToolCallContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '10px',
  border: '1px solid rgba(23, 81, 208, 0.12)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  animation: `${fadeIn} 0.3s ease-out`,
})

const ToolCallSpinner = styled('div')({
  width: '24px',
  height: '24px',
  border: '3px solid #f3f3f3',
  borderTop: '3px solid #1751D0',
  borderRadius: '50%',
  animation: `${spin} 1s linear infinite`,
  flexShrink: 0,
})

const ToolCallText = styled(Typography)({
  fontSize: '13px',
  lineHeight: '18px',
  color: '#666',
})

/**
 * Get a friendly name for a tool call
 */
const getToolCallFriendlyName = (toolName: string, translate: (key: string) => string): string => {
  const toolNameMap: Record<string, string> = {
    run_auto_design: translate('Running auto-design...'),
    save_system_design_as_template: translate('Saving template...'),
    navigate: translate('Navigating...'),
    load_auto_design_result: translate('Loading results...'),
    search_records: translate('Searching...'),
    create_or_update_system_template: translate('Creating template...'),
    lookup_component: translate('Looking up component...'),
  }

  return toolNameMap[toolName] || translate('Executing action...')
}

const AdaVoiceIndicator: React.FC<VoiceIndicatorProps> = ({
  isListening,
  isResponding,
  isPreparingResponse = false,
  reasoningStatus = null,
  speakingTranscript = null,
  toolCall,
  isSessionActive,
}) => {
  const classes = useStyles()
  const translate = useTranslate()
  const messages = useSelector(chatDrawerSelectors.getChatDrawerMessages)

  const [readyMessageIndex, setReadyMessageIndex] = React.useState(0)
  const readyMessages = React.useMemo(
    () => [
      translate('Ada is getting ready...'),
      translate('Ada is preparing...'),
      translate('Ada is warming up...'),
      translate('Ada is almost ready...'),
    ],
    [translate]
  )

  const preambleText = reasoningStatus || translate('Ada is preparing a response...')
  const showReasoningPreamble = isPreparingResponse && !isResponding

  const renderReasoningPreamble = () => {
    if (!showReasoningPreamble) {
      return null
    }
    return (
      <div style={{ width: '100%', maxWidth: 320, marginTop: 4 }}>
        <AdaShimmerText text={preambleText} />
      </div>
    )
  }

  const renderSpeakingCaption = () => {
    const caption = speakingTranscript?.trim()
    if (!caption) {
      return null
    }
    return (
      <div style={{ width: '100%', maxWidth: 360, marginTop: 4 }}>
        <AdaShimmerText text={caption} animateChanges={false} />
      </div>
    )
  }

  React.useEffect(() => {
    if (isSessionActive && !isListening && !isResponding && !isPreparingResponse && !toolCall) {
      const interval = setInterval(() => {
        setReadyMessageIndex((prev) => {
          if (prev >= readyMessages.length - 1) {
            return prev
          }
          return prev + 1
        })
      }, 2000)

      return () => clearInterval(interval)
    }
  }, [isSessionActive, isListening, isResponding, isPreparingResponse, toolCall, readyMessages.length])

  // Find all active auto-design toolExecutionStates (stack multiple if running simultaneously)
  const autoDesignStates = React.useMemo(() => {
    // Collect all messages with active auto-design toolExecutionState
    const states: Array<{ toolExecutionState: any; messageIndex: number }> = []
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (
        message.toolExecutionState &&
        message.toolExecutionState.toolType === 'autoDesign' &&
        (message.toolExecutionState.status === 'running' ||
          message.toolExecutionState.status === 'ready' ||
          message.toolExecutionState.status === 'completed' ||
          message.toolExecutionState.status === 'error')
      ) {
        states.push({ toolExecutionState: message.toolExecutionState, messageIndex: i })
      }
    }
    // Return in reverse order (most recent first) so they stack from top to bottom
    return states.reverse()
  }, [messages])

  const getIndicatorContent = () => {
    // If auto-design(s) are running, show the AutoDesignProgressItem(s) (same as chat)
    if (autoDesignStates.length > 0) {
      return (
        <>
          <div className={classes.indicatorContent}>
            <AdaAnimatedDots type="thinking" />
            {isListening
              ? translate('Listening...')
              : isResponding
                ? translate('Ada is responding...')
                : translate('Processing...')}
          </div>
          {autoDesignStates.map((state, index) => (
            <AutoDesignProgressItem
              key={`auto-design-${state.messageIndex}-${index}`}
              toolExecutionState={state.toolExecutionState}
              messageIndex={state.messageIndex}
            />
          ))}
        </>
      )
    }

    // If a tool call is active (but not auto-design), show the generic tool call indicator
    if (toolCall) {
      return (
        <>
          <div className={classes.indicatorContent}>
            <AdaAnimatedDots type="thinking" />
            {isListening
              ? translate('Listening...')
              : isResponding
                ? translate('Ada is responding...')
                : translate('Processing...')}
          </div>
          <ToolCallContainer>
            <ToolCallSpinner />
            <ToolCallText textVariant="body1" style={{ color: '#666' }}>
              {getToolCallFriendlyName(toolCall.name, translate)}
            </ToolCallText>
          </ToolCallContainer>
        </>
      )
    }

    // Otherwise show listening/responding/offline status
    let statusContent: React.ReactNode

    if (isListening) {
      statusContent = (
        <>
          <AdaAnimatedDots type="listening" />
          {translate('Listening...')}
        </>
      )
    } else if (isPreparingResponse && !isResponding) {
      statusContent = (
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 8, width: '100%' }}>
          <AdaAnimatedDots type="thinking" />
          {renderReasoningPreamble()}
        </div>
      )
    } else if (isResponding) {
      statusContent = (
        <>
          <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 10, width: '100%' }}>
            <AdaAnimatedDots type="thinking" />
            {renderSpeakingCaption()}
            <div
              style={{
                fontSize: '12px',
                opacity: 0.8,
              }}
            >
              {translate('Talk to interrupt Ada')}
            </div>
          </div>
        </>
      )
    } else {
      if (isSessionActive) {
        statusContent = (
          <>
            <AdaAnimatedDots type="ready" />
            {readyMessages[readyMessageIndex]}
          </>
        )
      } else {
        statusContent = (
          <>
            <AdaAnimatedDots type="offline" count={1} />
            {translate('Ada is offline. Please try again later.')}
          </>
        )
      }
    }

    return <div className={classes.indicatorContent}>{statusContent}</div>
  }

  return <div className={classes.voiceIndicatorContainer}>{getIndicatorContent()}</div>
}

export default AdaVoiceIndicator
